import styled from "styled-components";
import { pxToRem, withAlpha } from "../../styles/utils";

export const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${pxToRem(305)};
  max-width: min(${pxToRem(305)}, calc(100vw - ${pxToRem(32)}));
  padding: ${pxToRem(15)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 400;
  z-index: 30;
`;

export const FilterHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${pxToRem(10)};
  margin-bottom: ${pxToRem(12.5)};
`;

export const FilterTitle = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;
`;

export const FilterCount = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
  white-space: nowrap;
`;

export const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: ${pxToRem(10)};

  &::before {
    content: "";
    position: absolute;
    left: ${pxToRem(11.25)};
    top: 50%;
    width: ${pxToRem(12.5)};
    height: ${pxToRem(12.5)};
    transform: translateY(-58%);
    border: ${pxToRem(1.5)} solid ${({ theme }) => theme.colors.inkMuted};
    border-radius: 50%;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    left: ${pxToRem(23)};
    top: 58%;
    width: ${pxToRem(6)};
    height: ${pxToRem(1.5)};
    transform: rotate(45deg);
    transform-origin: left center;
    border-radius: ${pxToRem(2)};
    background: ${({ theme }) => theme.colors.inkMuted};
    pointer-events: none;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  height: ${pxToRem(42.5)};
  padding: 0 ${pxToRem(12.5)} 0 ${pxToRem(37.5)};
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  outline: none;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.inkMuted};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: inset 0 0 0 ${pxToRem(1.25)} ${({ theme }) => theme.colors.brand};
  }
`;

export const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: ${pxToRem(270)};
  margin: 0 ${pxToRem(-5)};
  padding: 0 ${pxToRem(5)};
  overflow-y: auto;
`;

export const OptionItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${pxToRem(11.25)};
  min-height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: ${pxToRem(8.75)} ${pxToRem(10)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 400;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.ink, 0.04)};
  }
`;

export const Checkbox = styled.input`
  appearance: none;
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  width: ${pxToRem(22.5)};
  height: ${pxToRem(22.5)};
  margin: 0;
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;

  &::before {
    content: "";
    width: ${pxToRem(12.5)};
    height: ${pxToRem(12.5)};
    clip-path: polygon(14% 44%, 0 60%, 40% 100%, 100% 22%, 84% 8%, 38% 68%);
    background: ${({ theme }) => theme.colors.white};
    transform: scale(0);
    transition: transform 0.1s ease;
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.brand};
  }

  &:checked::before {
    transform: scale(1);
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const OptionLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const OptionCount = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
`;

export const EmptyOptions = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${pxToRem(10)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  text-align: center;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(12.5)};
  padding-top: ${pxToRem(12.5)};
  border-top: ${pxToRem(1.25)} solid ${({ theme }) => theme.colors.inkBorder};
`;

const actionFocus = `
  outline-offset: ${pxToRem(2)};
`;

export const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  height: ${pxToRem(42.5)};
  padding: 0 ${pxToRem(17.5)};
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.brandDark};
  }

  &:focus-visible {
    ${actionFocus}
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${pxToRem(42.5)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.inkSoft};
  cursor: pointer;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    ${actionFocus}
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.inkMuted};
    cursor: not-allowed;
    opacity: 0.65;
  }
`;
